import * as React from 'react';
import type { FC } from 'react';
import InfiniteScroll from 'app/helpers/InfiniteScroll';
import type { Game } from 'app/CasinoGames/types';
import { State, PENDING } from 'admin-mojo/helpers/StateNotification';
import { useLocales } from '../locales';
import type { PaginatedGames } from '../types';
import GameComponent from './Game';
import type { GameStarter } from '../useGameStarter';

// The homepage stops loading more games after this many pages, so the visitor can still scroll
// down to the footer links instead of the grid growing endlessly.
const HOMEPAGE_MAX_ADDITIONAL_PAGES = 2;

const Grid: FC<{
  paginatedGames: PaginatedGames;
  resultsVersion: number;
  searchParams: URLSearchParams;
  // The page address of the current results, for the crawlable next-page link.
  pagePath: string;
  state: State;
  gameStarter: GameStarter;
  playerPresent: boolean;
  homepage: boolean;
}> = ({ paginatedGames, resultsVersion, searchParams, pagePath, state, gameStarter, playerPresent, homepage }) => {
  const locales = useLocales();
  const loading = state.status === PENDING;

  // While a filter change loads, the current results (games or the no-games message) stay in
  // place, dimmed, under a loading status - see .xo-games-results in _games.scss.
  const content = paginatedGames?.data?.length ? (
    <div className="casino-games">
      {/* InfiniteScroll reads its items only on mount, so each new result set remounts it */}
      <InfiniteScroll<Game>
        key={resultsVersion}
        initialItems={paginatedGames}
        url="/casino/search"
        params={searchParams}
        maxAdditionalPages={homepage ? HOMEPAGE_MAX_ADDITIONAL_PAGES : undefined}
        nextPageLink={{
          path: pagePath,
          text: locales('next_page'),
          className: 'xo-button xo-button_faded xo-games_next-page',
        }}
      >
        {(currentGames) => (
          <div className="xo-games_list">
            {currentGames.map((game) => (
              <GameComponent key={game.id} game={game} gameStarter={gameStarter} playerPresent={playerPresent} />
            ))}
          </div>
        )}
      </InfiniteScroll>
    </div>
  ) : (
    <div className="xo-games-empty">
      <div className="xo-games-empty_title">{locales('no_games_1')}</div>
      <div>{locales('no_games_2')}</div>
    </div>
  );

  return (
    <div className={`xo-games-results${loading ? ' xo-games-results__loading' : ''}`} aria-busy={loading}>
      <div className="xo-games-results_status" role="status">
        {loading && (
          <span className="xo-games-results_indicator">
            <span className="xo-games-results_spinner" role="img" aria-label={locales('loading')} />
          </span>
        )}
      </div>
      <div className="xo-games-results_content">{content}</div>
    </div>
  );
};

export default Grid;
